/**
 * Tenant Brand & White-Label Profile.
 *
 * Scoped to the tenant scope so each tenant/subscriber can have their own
 * brand name, custom logo, theme accent color, portal subheadings, and
 * white-label attribution toggles.
 */

import { tenantGetJSON, tenantSetJSON } from '@/product/lib/tenantStorage';

export interface TenantWhiteLabelConfig {
    /** Subscriber / Company Display Name */
    companyName: string;
    /** Public tagline or trade moniker */
    tagline: string;
    /** Image data URL or hosted URL for wordmark / logo */
    logoUrl: string | null;
    /** Square icon / favicon URL */
    iconUrl: string | null;
    /** Hex color for tenant branding accents, e.g. #0284c7 */
    brandColor: string;
    /** Show or hide 'Powered by SiteLine' in external customer & signer portals */
    showPoweredBy: boolean;
    /** Custom support or office phone shown to customers */
    supportPhone: string;
    /** Custom support or office email shown to customers */
    supportEmail: string;
    /** Custom website */
    websiteUrl: string;
    /** Portal custom header text */
    portalHeaderTitle: string;
    /** Portal greeting message */
    portalGreeting: string;
}

export const TENANT_BRANDING_KEY = 'siteline_tenant_branding_v1';

export const DEFAULT_TENANT_BRANDING: TenantWhiteLabelConfig = {
    companyName: '',
    tagline: 'Quality Restoration & Contracting Services',
    logoUrl: null,
    iconUrl: null,
    brandColor: '#2563eb', // Default solid blue
    showPoweredBy: true,
    supportPhone: '',
    supportEmail: '',
    websiteUrl: '',
    portalHeaderTitle: 'Customer Project Portal',
    portalGreeting: 'Track live milestones, daily photos, and approvals for your project.',
};

export function getTenantBranding(): TenantWhiteLabelConfig {
    if (typeof window === 'undefined') return { ...DEFAULT_TENANT_BRANDING };
    const stored = tenantGetJSON<Partial<TenantWhiteLabelConfig> | null>(
        TENANT_BRANDING_KEY,
        null
    );
    if (!stored) {
        return { ...DEFAULT_TENANT_BRANDING };
    }
    return { ...DEFAULT_TENANT_BRANDING, ...stored };
}

export function saveTenantBranding(
    partial: Partial<TenantWhiteLabelConfig>
): TenantWhiteLabelConfig {
    const current = getTenantBranding();
    const updated = { ...current, ...partial };
    tenantSetJSON(TENANT_BRANDING_KEY, updated);

    // Apply primary brand color variable to root document if valid hex
    if (typeof document !== 'undefined' && updated.brandColor) {
        document.documentElement.style.setProperty('--brand-primary', updated.brandColor);
    }

    return updated;
}
